import { useState } from "react";
import {
  EditButton,
  Datagrid,
  DateField,
  List,
  TextField,
  TextInput,
  BooleanInput,
  CheckboxGroupInput,
  SelectInput,
  NumberInput,
  TopToolbar,
  FilterButton,
  CreateButton,
  Button,
  BulkDeleteButton,
  useListContext,
  useDataProvider,
} from "react-admin";
import { useMutation } from "@tanstack/react-query";
import { Box, CircularProgress } from "@mui/material";
import {
  Campaign as CampaignIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

import { serviceNameList, enrichmentActivitiesList } from "../../utils";
import PaginationOptions from "../../components/pagination-options";
import OperationRadiusField from "../../components/operation-radius-field";
import { IdArrayInput } from "../../components";
import providerExporter from "./exporter";

const postFilters = [
  <TextInput
    source="query"
    label="Provider Name"
    variant="outlined"
    alwaysOn
  />,
  <SelectInput
    source="performsService"
    label="Service"
    variant="outlined"
    choices={serviceNameList}
    alwaysOn
  />,
  <BooleanInput
    source="isActive"
    label="Active"
    alwaysOn
    sx={{ marginBottom: "10px", marginLeft: "4px" }}
  />,
  <BooleanInput
    source="isSubscriber"
    label="Subscriber"
    alwaysOn
    sx={{ marginBottom: "10px", marginLeft: "4px" }}
  />,

  <IdArrayInput source="ids" label="IDs" sx={{ minWidth: "230px" }} />,
  <TextInput label="E-mail" source="email" variant="outlined" />,
  <TextInput label="Phone Number" source="phoneNumber" variant="outlined" />,
  <BooleanInput label="Has Public (SaaS) Profile" source="hasPublicProfile" />,
  <BooleanInput label="Dogs OK?" source="allowsDogs" />,
  <BooleanInput label="Cats OK?" source="allowsCats" />,
  <BooleanInput label="Smoking OK?" source="allowsSmoking" />,
  <BooleanInput label="Has Car?" source="hasCar" />,
  <BooleanInput
    label="Has special needs experience?"
    source="hasSpecialNeedsExperience"
  />,
  <CheckboxGroupInput
    label="Enrichment Activities"
    source="enrichmentActivities"
    choices={enrichmentActivitiesList}
  />,
  <NumberInput
    min={0}
    max={18}
    label="Child Age"
    source="childAge"
    variant="outlined"
  />,
];

// Custom export button that fetches all providers with pagination
// If rows are selected, exports only selected; otherwise exports all
const ProviderListActions = () => {
  const { selectedIds, filterValues, sort } = useListContext();
  const dataProvider = useDataProvider();
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch all data with pagination to handle large datasets
      const pageSize = 1000;
      let allData = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const { data, total } = await dataProvider.getList("Provider", {
          pagination: { page, perPage: pageSize },
          sort: sort || { field: "created", order: "DESC" },
          filter: filterValues || {},
        });

        allData = [...allData, ...(data || [])];
        hasMore = allData.length < total;
        page++;
      }

      if (selectedIds && selectedIds.length > 0) {
        // Export only selected rows
        const selectedRecords = allData.filter((record) =>
          selectedIds.includes(record.id),
        );
        providerExporter(selectedRecords);
      } else {
        // Export all rows
        providerExporter(allData);
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <TopToolbar>
      <CreateButton />
      <FilterButton />
      <Button
        onClick={handleExport}
        disabled={exporting}
        label={
          exporting
            ? "Exporting..."
            : selectedIds?.length > 0
              ? `Export (${selectedIds.length})`
              : "Export"
        }
      >
        {exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
      </Button>
    </TopToolbar>
  );
};

const BulkActionButtons = () => {
  const { selectedIds, filterValues, sort } = useListContext();
  const dataProvider = useDataProvider();
  const [exporting, setExporting] = useState(false);

  const { mutate } = useMutation({
    mutationFn: dataProvider.promoteProviders,
  });

  const promoteProviders = () => {
    mutate(selectedIds);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const pageSize = 1000;
      let allData = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const { data, total } = await dataProvider.getList("Provider", {
          pagination: { page, perPage: pageSize },
          sort: sort || { field: "created", order: "DESC" },
          filter: filterValues || {},
        });

        allData = [...allData, ...(data || [])];
        hasMore = allData.length < total;
        page++;
      }

      // Export only selected rows
      const selectedRecords = allData.filter((record) =>
        selectedIds.includes(record.id),
      );
      providerExporter(selectedRecords);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Button onClick={promoteProviders}>
        <>
          <CampaignIcon sx={{ marginRight: "8px" }} />
          Promote
        </>
      </Button>
      <BulkDeleteButton />
      <Button
        onClick={handleExport}
        disabled={exporting}
        label={exporting ? "Exporting..." : `Export (${selectedIds?.length})`}
      >
        {exporting ? <CircularProgress size={18} /> : <DownloadIcon />}
      </Button>
    </Box>
  );
};

const UserList = (props) => {
  return (
    <List
      {...props}
      storeKey="provider-all-providers-list"
      pagination={<PaginationOptions />}
      perPage={25}
      filters={postFilters}
      filterDefaultValues={{ isActive: true, isSubscriber: false }}
      sort={{ field: "created", order: "DESC" }}
      exporter={providerExporter}
      actions={<ProviderListActions />}
      disableSyncWithLocation={true}
    >
      <Datagrid rowClick="show" bulkActionButtons={<BulkActionButtons />}>
        <TextField source="firstName" />
        <TextField source="lastName" />
        <TextField source="email" label="E-mail" />
        <TextField source="address.zipcode" label="Zipcode" />
        <DateField source="created" label="Profile Created" />
        <TextField source="phoneNumber" />
        <OperationRadiusField source="Operation Raduis" labeled={false} />
        <TextField source="becomingProviderLastStep" />
        <EditButton />
      </Datagrid>
    </List>
  );
};

export default UserList;
